import { useEffect, useState } from 'react';

const SCRIPT_ID = 'krypton-client-sdk';
const STYLESHEET_ID = 'krypton-client-theme';

/**
 * Charge dynamiquement le SDK Krypton Client (Lyra / SogeCommerce).
 *
 * Krypton fonctionne en lisant l'attribut `kr-public-key` sur la balise script
 * AU MOMENT du chargement. Il faut donc :
 *   1. Créer la balise <script> avec l'attribut kr-public-key AVANT appendChild
 *   2. Attendre à la fois script.onload ET window.KR (Krypton peuple window.KR
 *      de manière asynchrone après l'exécution du script)
 */
export const useKryptonClient = ({ publicKey, endpoint }) => {
    const [ready, setReady] = useState(() => typeof window !== 'undefined' && Boolean(window.KR));
    const [error, setError] = useState(null);

    useEffect(() => {
        if (typeof window === 'undefined' || !publicKey) return;
        if (window.KR) { setReady(true); return; }

        const base = (endpoint || 'https://sogecommerce.societegenerale.eu').replace(/\/$/, '');
        const scriptUrl = `${base}/static/js/krypton-client/V4.0/stable/kr-payment-form.min.js`;
        const stylesheetUrl = `${base}/static/js/krypton-client/V4.0/ext/classic-reset.css`;

        if (!document.getElementById(STYLESHEET_ID)) {
            const link = document.createElement('link');
            link.id = STYLESHEET_ID;
            link.rel = 'stylesheet';
            link.href = stylesheetUrl;
            document.head.appendChild(link);
        }

        const existing = document.getElementById(SCRIPT_ID);
        if (existing) {
            existing.remove();
            delete window.KR;
        }

        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.setAttribute('kr-public-key', publicKey);
        script.setAttribute('kr-language', 'fr-FR');
        script.src = scriptUrl;
        script.onerror = () => setError(new Error('Impossible de charger le SDK de paiement.'));

        script.onload = () => {
            const start = Date.now();
            const poll = () => {
                if (window.KR) {
                    setReady(true);
                    return;
                }
                if (Date.now() - start > 10_000) {
                    setError(new Error('SDK Krypton chargé mais window.KR absent après 10s'));
                    return;
                }
                setTimeout(poll, 50);
            };
            poll();
        };

        document.head.appendChild(script);
    }, [publicKey, endpoint]);

    return { ready, error };
};
